import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';
import { environment } from '../../environments/environment';
import { FeatureService } from '../core/tenant/feature.service';
import { SessionService } from '../core/tenant/session.service';

/** Fila de `GET /account/users?rol=vendedor`. Con nombre para que el demo local conteste con este mismo tipo. */
export interface FilaDeVendedor {
  id: number;
  email: string;
  nombre?: string | null;
}

export interface Vendedor {
  id: number;
  email: string;
  nombre: string | null;
}

/**
 * F1.10 — quién vendió.
 *
 * - Si la sesión es de un **vendedor**, la venta es suya y no se elige. Si el
 *   login trae su `users.id` (`userId`), la venta lo lleva explícito: así queda a
 *   su nombre aunque la sincronice otra persona (decisión de ECM). Si no lo trae,
 *   no se manda y -mt la pone a nombre del token que sincroniza (limitación
 *   conocida, pedida a Agente-mt).
 * - Si es **cajero o admin**, elige de `GET /account/users?rol=vendedor` o deja
 *   «Sin asignar». La elección se queda para las ventas siguientes del turno.
 */
@Injectable({ providedIn: 'root' })
export class VendedoresService {
  private http = inject(HttpClient);
  private features = inject(FeatureService);
  private session = inject(SessionService);

  readonly vendedores = signal<Vendedor[]>([]);
  /**
   * El vendedor de las ventas. Se elige desde la barra lateral y SE QUEDA hasta que alguien lo cambie, también tras
   * recargar o volver a entrar (Santiago, 20/09: «el vendedor no tiende a cambiar muy seguido… que una vez
   * seleccionado se mantenga hasta que se cambie»; ECM: se guarda en el equipo). Antes vivía solo en memoria y al
   * reabrir la caja quedaba en blanco.
   */
  readonly elegido = signal<Vendedor | null>(this.guardado());

  private clave(): string | null {
    const negocio = this.session.session()?.tenantId;
    return negocio ? `suresell.vendedor.${negocio}` : null;
  }

  /** Lo guardado en ESTE equipo para ESTE negocio; sin nada, ninguno. */
  private guardado(): Vendedor | null {
    try {
      const clave = this.clave();
      const crudo = clave ? localStorage.getItem(clave) : null;
      const v = crudo ? JSON.parse(crudo) : null;
      return v && typeof v.id === 'number' ? { id: v.id, email: v.email ?? '', nombre: v.nombre ?? null } : null;
    } catch {
      return null;
    }
  }

  private recordar(v: Vendedor | null): void {
    const clave = this.clave();
    if (!clave) return;
    try {
      if (v) localStorage.setItem(clave, JSON.stringify(v));
      else localStorage.removeItem(clave);
    } catch { /* sin almacenamiento: se queda solo en esta pestaña */ }
  }
  readonly esVendedor = computed(() => this.session.session()?.role === 'vendedor');

  async cargar(): Promise<void> {
    if (!this.features.has('mayorista') || this.esVendedor()) {
      this.vendedores.set([]);
      return;
    }
    try {
      const filas = await firstValueFrom(
        this.http.get<FilaDeVendedor[]>(
          `${environment.apiBaseUrl}/account/users`,
          { params: { rol: 'vendedor' } },
        ),
      );
      this.vendedores.set((filas ?? []).map((f) => ({ id: f.id, email: f.email, nombre: f.nombre ?? null })));
      this.releerGuardado();
      const actual = this.elegido();
      // Uno que ya no está en la lista del negocio no se queda elegido en silencio.
      if (actual && !this.vendedores().some((v) => v.id === actual.id)) this.elegir(null);
    } catch {
      // Sin red se conserva la lista y la elección que ya había.
    }
  }

  elegir(id: number | null): void {
    const v = id == null ? null : (this.vendedores().find((x) => x.id === id) ?? null);
    this.elegido.set(v);
    this.recordar(v);
  }

  /**
   * El selector, fuera de la venta (Santiago y ECM, 20/09). Lo abren la barra lateral y el nombre de la franja fija de
   * la caja: el mismo diálogo desde los dos sitios, para que no haya dos formas de elegir lo mismo.
   */
  async abrirSelector(): Promise<void> {
    const lista = this.vendedores();
    const elegido = this.elegido();
    const esc = (t: string) => t.replace(/[<>&]/g, '');
    const opciones = lista.map((v) =>
      `<option value="${v.id}" ${elegido?.id === v.id ? 'selected' : ''}>${esc(VendedoresService.etiqueta(v, lista))}</option>`).join('');
    const { value, isConfirmed } = await Swal.fire({
      title: 'Vendedor de las ventas',
      // Santiago (26/09): tamaño normal, como los demás selectores de los diálogos del POS (clase swal2-input, sin ancho a mano).
      html: '<select id="v-sel" class="swal2-input">'
        + `<option value="" ${elegido ? '' : 'selected'}>Sin asignar</option>${opciones}</select>`
        + '<p style="font-size:.8rem;color:#64748b;margin-top:.5rem">Se queda puesto hasta que lo cambies.</p>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => (document.getElementById('v-sel') as HTMLSelectElement)?.value ?? '',
    });
    if (!isConfirmed) return;
    this.elegir(value ? Number(value) : null);
  }

  /** Al volver a entrar, el guardado se relee: la sesión no existía cuando se construyó el servicio. */
  releerGuardado(): void {
    if (!this.elegido()) this.elegido.set(this.guardado());
  }

  /** Lo que viaja en la venta: el id elegido, o nada. */
  vendedorIdParaLaVenta(): number | undefined {
    if (this.esVendedor()) {
      const propio = this.session.session()?.userId;
      return typeof propio === 'number' ? propio : undefined;
    }
    return this.elegido()?.id;
  }

  /**
   * Lo que se lee en el selector de la caja.
   *
   * **Nunca el correo** (ECM, 17/09): en un mayorista ese desplegable lo ve quien esté al otro lado del mostrador,
   * y un correo ahí es un dato personal enseñado por accidente. El vendedor **no se quita de la lista**: uno que
   * desaparece es un defecto peor que un nombre feo, y quien lo busque no sabría por qué no está. El nombre se
   * arregla en el panel, que es donde se crea.
   *
   * Con `todos`, si hay **más de uno sin nombre** se añade su número: dos «Sin nombre» idénticos en el desplegable
   * harían que el cajero atribuyera la venta a la persona equivocada, que es peor que enseñar un correo. Con uno
   * solo no se añade nada, que es el caso normal.
   */
  static etiqueta(v: Vendedor, todos?: readonly Vendedor[]): string {
    const nombre = v.nombre?.trim();
    if (nombre) return nombre;
    const sinNombre = (todos ?? []).filter((o) => !o.nombre?.trim()).length;
    return sinNombre > 1 ? `Sin nombre · N.º ${v.id}` : 'Sin nombre';
  }
}
